import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, Checkbox, Chip, FormControlLabel, LinearProgress, ListSubheader, MenuItem, Select, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import PayrollService, {
  BulkOp, BulkPreviewRow, CommonSetup, PayRuleKind, PayTemplate, PaySetup, Person, describeError,
} from '../../api/services/payroll.service';
import { RULE_KINDS_EARN, RULE_KINDS_OTHER, RULE_LABEL } from './payrollUI';

// One change to several people's pay at once.
//
// Pick the change, see every selected person's rules before and after (the
// server works it out, so it reads exactly like the single editor), then
// apply. People the change does not touch are shown and left alone. Each
// save goes through the same path as editing one person: their draft weeks
// recalculate and it is audited.

type Change = 'template' | 'set_amount' | 'raise_amount' | 'add_rule' | 'remove_kind';

const CHANGE_LABEL: Record<Change, string> = {
  template: 'Apply a template',
  set_amount: 'Set an amount',
  raise_amount: 'Raise or lower an amount',
  add_rule: 'Add a rule',
  remove_kind: 'Remove a rule',
};

const num = (s: string) => (s.trim() === '' ? null : Number(s));

const BulkRuleEditor: React.FC<{
  selected: Person[];
  onClear: () => void;
  onApplied: () => void;
}> = ({ selected, onClear, onApplied }) => {
  const [change, setChange] = useState<Change>('template');
  const [templates, setTemplates] = useState<{ saved: PayTemplate[]; common: CommonSetup[] }>({ saved: [], common: [] });
  const [setupKey, setSetupKey] = useState('');
  const [kind, setKind] = useState<PayRuleKind | ''>('');
  const [amount, setAmount] = useState('');
  const [threshold, setThreshold] = useState('');
  const [label, setLabel] = useState('');
  const [preview, setPreview] = useState<BulkPreviewRow[] | null>(null);
  const [fill, setFill] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => { PayrollService.templates().then(setTemplates).catch(() => undefined); }, []);

  const setups: { value: string; label: string; setup: PaySetup }[] = useMemo(() => [
    ...templates.saved.map((t) => ({ value: `t:${t.id}`, label: t.name, setup: t })),
    ...templates.common.map((c, i) => ({ value: `c:${i}`, label: c.description, setup: c })),
  ], [templates]);

  // The kinds the selected people actually have, for set/raise/remove.
  const presentKinds = useMemo(() => {
    const seen = new Set<PayRuleKind>();
    selected.forEach((p) => p.rules.forEach((r) => seen.add(r.kind)));
    return [...RULE_KINDS_EARN, ...RULE_KINDS_OTHER].filter((k) => seen.has(k));
  }, [selected]);
  const allKinds = [...RULE_KINDS_EARN, ...RULE_KINDS_OTHER];

  // The change as the server takes it, or null while it is incomplete.
  const op: BulkOp | null = useMemo(() => {
    const a = num(amount);
    switch (change) {
      case 'template': {
        const s = setups.find((x) => x.value === setupKey)?.setup;
        return s ? { type: 'template', rules: s.rules, ...(s.stopKinds?.length && { stopKinds: s.stopKinds }), daysThatCount: s.daysThatCount } : null;
      }
      case 'set_amount': return kind && a !== null && Number.isFinite(a) && a >= 0 ? { type: 'set_amount', kind, amount: a } : null;
      case 'raise_amount': return kind && a !== null && Number.isFinite(a) && a !== 0 ? { type: 'raise_amount', kind, by: a } : null;
      case 'add_rule': {
        if (!kind || a === null || !Number.isFinite(a) || a < 0) return null;
        const t = num(threshold);
        return {
          type: 'add_rule',
          rule: {
            kind,
            amount: a,
            ...((kind === 'over_threshold' || kind === 'bonus') && t !== null && Number.isFinite(t) && { threshold: t }),
            ...(kind === 'over_threshold' && { period: 'day' as const }),
            ...(kind === 'bonus' && { metric: 'days' as const }),
            ...(label.trim() && { label: label.trim() }),
          },
        };
      }
      case 'remove_kind': return kind ? { type: 'remove_kind', kind } : null;
      default: return null;
    }
  }, [change, setupKey, setups, kind, amount, threshold, label]);

  // By value, so the list refreshing after an apply re-previews (everyone
  // now reads "No change") without counting as a different selection.
  const idsKey = selected.map((p) => p.accountId).join(',');
  const ids = useMemo(() => idsKey.split(',').filter(Boolean), [idsKey]);
  const opKey = JSON.stringify(op);
  useEffect(() => { setDone(null); }, [opKey]);
  useEffect(() => {
    if (!op) { setPreview(null); return undefined; }
    let active = true;
    const t = window.setTimeout(() => {
      PayrollService.bulkPreview(ids, op)
        .then((r) => { if (active) { setPreview(r.people); setError(null); } })
        .catch((err) => { if (active) { setPreview(null); setError(describeError(err, 'Could not work that out.')); } });
    }, 300);
    return () => { active = false; window.clearTimeout(t); };
  }, [opKey, ids, selected]); // eslint-disable-line react-hooks/exhaustive-deps

  const changing = (preview || []).filter((r) => r.changed);
  const unpaid = changing.reduce((t, r) => t + r.unpaidFinalizedWeeks, 0);

  const apply = async () => {
    if (!op) return;
    setBusy(true);
    setError(null);
    try {
      const r = await PayrollService.bulkApply(ids, op, fill && unpaid > 0);
      const parts = [`${r.changed} ${r.changed === 1 ? 'person' : 'people'} updated`];
      if (r.refreshedDrafts) parts.push('draft weeks recalculated');
      if (r.filledFinalized) parts.push(`${r.filledFinalized} finalized ${r.filledFinalized === 1 ? 'week' : 'weeks'} filled in`);
      setDone(`${parts.join(', ')}.`);
      onApplied();
    } catch (err) {
      setError(describeError(err, 'The change could not be applied.'));
    } finally {
      setBusy(false);
    }
  };

  const pickChange = (c: Change) => { setChange(c); setKind(''); setAmount(''); setThreshold(''); setLabel(''); setSetupKey(''); };
  const kindsFor = change === 'add_rule' ? allKinds : presentKinds;

  return (
    <Stack gap={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
        <Box>
          <Typography variant="h3" sx={{ lineHeight: 1.2 }}>Change pay for {selected.length} {selected.length === 1 ? 'person' : 'people'}</Typography>
          <Typography variant="caption" color="text.secondary">One change, checked person by person before it is saved.</Typography>
        </Box>
        <Button onClick={onClear}>Clear selection</Button>
      </Stack>
      <Stack direction="row" gap={0.75} flexWrap="wrap">
        {selected.slice(0, 12).map((p) => <Chip key={p.accountId} size="small" label={p.name} />)}
        {selected.length > 12 && <Chip size="small" variant="outlined" label={`+${selected.length - 12} more`} />}
      </Stack>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {done && <Alert severity="success" onClose={() => setDone(null)}>{done}</Alert>}

      <Card sx={{ p: 2 }}>
        <Stack gap={1.5}>
          <Select size="small" value={change} onChange={(e) => pickChange(e.target.value as Change)} inputProps={{ 'aria-label': 'Change' }} sx={{ maxWidth: 320 }}>
            {(Object.keys(CHANGE_LABEL) as Change[]).map((c) => <MenuItem key={c} value={c}>{CHANGE_LABEL[c]}</MenuItem>)}
          </Select>

          {change === 'template' && (
            <Stack gap={0.75}>
              <Select size="small" displayEmpty value={setupKey} onChange={(e) => setSetupKey(String(e.target.value))} inputProps={{ 'aria-label': 'Template' }} renderValue={(v) => (v ? setups.find((x) => x.value === v)?.label : <Typography component="span" color="text.secondary">Pick a template</Typography>)}>
                {templates.saved.length > 0 && <ListSubheader>Saved templates</ListSubheader>}
                {setups.filter((x) => x.value.startsWith('t:')).map((x) => <MenuItem key={x.value} value={x.value}>{x.label}</MenuItem>)}
                {templates.common.length > 0 && <ListSubheader>Common setups in this company</ListSubheader>}
                {setups.filter((x) => x.value.startsWith('c:')).map((x) => <MenuItem key={x.value} value={x.value} sx={{ whiteSpace: 'normal' }}>{x.label}</MenuItem>)}
              </Select>
              <Typography variant="caption" color="text.secondary">Replaces each person&apos;s rules with a copy of the template.</Typography>
            </Stack>
          )}

          {change !== 'template' && (
            <Stack direction="row" gap={1.25} flexWrap="wrap" alignItems="flex-start">
              <Select size="small" displayEmpty value={kind} onChange={(e) => setKind(e.target.value as PayRuleKind)} inputProps={{ 'aria-label': 'Rule' }} sx={{ minWidth: 220 }} renderValue={(v) => (v ? RULE_LABEL[v as PayRuleKind] : <Typography component="span" color="text.secondary">Which rule</Typography>)}>
                {kindsFor.length === 0 && <MenuItem disabled>None of them has rules yet</MenuItem>}
                {kindsFor.map((k) => <MenuItem key={k} value={k}>{RULE_LABEL[k]}</MenuItem>)}
              </Select>
              {(change === 'set_amount' || change === 'raise_amount' || change === 'add_rule') && (
                <TextField
                  size="small"
                  label={change === 'raise_amount' ? 'By (use − to lower)' : 'Amount'}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^0-9.-]/g, ''))}
                  inputProps={{ inputMode: 'decimal' }}
                  InputProps={{ startAdornment: <Typography component="span" color="text.secondary" sx={{ mr: 0.5 }}>$</Typography> }}
                  sx={{ width: 170 }}
                />
              )}
              {change === 'add_rule' && (kind === 'over_threshold' || kind === 'bonus') && (
                <TextField size="small" label={kind === 'bonus' ? 'Days to reach' : 'Stops a day above'} value={threshold} onChange={(e) => setThreshold(e.target.value.replace(/[^0-9]/g, ''))} sx={{ width: 160 }} />
              )}
              {change === 'add_rule' && (kind === 'deduction' || kind === 'bonus') && (
                <TextField size="small" label="Name on the receipt" placeholder={kind === 'deduction' ? 'Van rent' : '6th day bonus'} value={label} onChange={(e) => setLabel(e.target.value)} sx={{ width: 200 }} />
              )}
            </Stack>
          )}
        </Stack>
      </Card>

      {preview && (
        <Card sx={{ overflow: 'hidden' }}>
          <Stack direction="row" justifyContent="space-between" sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography sx={{ fontWeight: 700 }}>{changing.length} of {preview.length} change</Typography>
          </Stack>
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Person</TableCell>
                  <TableCell>Now</TableCell>
                  <TableCell>After</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.map((r) => (
                  <TableRow key={r.accountId} sx={{ opacity: r.changed ? 1 : 0.5 }}>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{r.name}{r.status === 'former' && <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}> · former</Box>}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{r.before || 'No rules'}</TableCell>
                    <TableCell sx={{ fontWeight: r.changed ? 600 : 400 }}>{r.changed ? (r.after || 'No rules') : 'No change'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {unpaid > 0 && (
        <FormControlLabel
          control={<Checkbox checked={fill} onChange={(e) => setFill(e.target.checked)} />}
          label={`Also fill the ${unpaid} finalized ${unpaid === 1 ? 'week' : 'weeks'} where these people were paid nothing (no rules then). Weeks they were paid in never change.`}
        />
      )}
      {busy && <LinearProgress />}
      <Stack direction="row" justifyContent="flex-end">
        <Button variant="contained" disableElevation disabled={!op || !changing.length || busy} onClick={apply}>
          {changing.length ? `Apply to ${changing.length} ${changing.length === 1 ? 'person' : 'people'}` : 'Apply'}
        </Button>
      </Stack>
    </Stack>
  );
};

export default BulkRuleEditor;
